import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import JobService from "../../api/JobService";
import { RootState } from "../../store";
import { checkIsRunning } from "../../store/clock-slice";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import { CustomInput } from "../CustomInput/CustomInput";
import { MaterialsList } from "../MaterialsList/MaterialsList";
import Modal from "../Modal/Modal";

import styles from "./other-appointment.module.css";

type OtherAppointmentProps = {
  error?: string;
};

export const OtherAppointment = ({
  error = undefined,
}: OtherAppointmentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [jobId, setJobId] = useState("");
  const [products, setProducts] = useState<any>(null);
  const [webProductsPDF, setWebProductsPDF] = useState<any>(null);
  const [sevdeskProductsPDF, setSevdeskProductsPDF] = useState<any>(null);
  const [pausedServiceModalOpen, setIsPausedServiceModalOpen] = useState(false);

  const appointment = useSelector((state: RootState) => state.appointment);
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const isOnline = useSelector((state: RootState) => state.online.isOnline);

  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );

  useEffect(() => {
    if (appointment.jobs.length > 0) {
      const copiedJobs = Array.from(appointment.jobs);
      const lastJobId = copiedJobs.pop();
      setJobId(lastJobId.id);
    }
  }, [appointment.jobs]);

  const fetchGroupedMaterialsByJobPDF = useCallback(async () => {
    if (jobId) {
      const { data } = await JobService.GetGroupedMaterialsByJobPDF(jobId);
      if (data.webProducts) {
        setWebProductsPDF(
          data.webProducts.map((p: any) => ({
            id: p.item.productId,
            name: p.item.product.name,
            quantity: p.quantity,
            description: p.item.product.description,
            payExtraQuantity: p.payExtraQuantity ?? 0,
          }))
        );
      }

      if (data.sevdeskProducts) {
        setSevdeskProductsPDF(
          data.sevdeskProducts.map((p: any) => ({
            id: p.item.id,
            name: p.item.name,
            quantity: p.quantity,
            description: p.item.description,
            payExtraQuantity: p.payExtraQuantity ?? 0,
          }))
        );
      }
    }
  }, [jobId]);

  useEffect(() => {
    const combinedProductsPDF = [
      ...(webProductsPDF || []),
      ...(sevdeskProductsPDF || []),
    ];

    setProducts(combinedProductsPDF);
    dispatch(creatingDocumentationActions.setProducts(combinedProductsPDF));
  }, [webProductsPDF, sevdeskProductsPDF, dispatch]);

  useEffect(() => {
    if (isOnline) {
      fetchGroupedMaterialsByJobPDF();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchGroupedMaterialsByJobPDF, isOnline, appointment]);

  const handleMaterialsClicked = () => {
    if (isRunning) navigate("/products", { state: { editMode: true } });
    else setIsPausedServiceModalOpen(true);
  };

  const handleTextareaChange = (note: string) => {
    if (isRunning) dispatch(creatingDocumentationActions.setRemark(note));
    else setIsPausedServiceModalOpen(true);
  };

  return (
    <div className={styles["content"]}>
      <Modal
        isOpen={pausedServiceModalOpen}
        note="TimerIsStopped"
        onConfirm={() => setIsPausedServiceModalOpen(false)}
      />

      <MaterialsList
        title={"Materialeinsatz"}
        data={products}
        readOnly={true}
        plusIcon
        onClick={handleMaterialsClicked}
      />

      <CustomInput
        label={t("Remark")}
        placeholder={"Zusätzlicher Text"}
        hideTick
        onInputChange={handleTextareaChange}
      />
      {error && <div className={styles["error-message"]}>{error}</div>}
    </div>
  );
};
